import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../css/Landing.css";

const API_BASE =
  (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");

const BOOT_SIZES_UK = [
  "10","10.5","11","11.5","12","12.5","13","13.5",
  "1","1.5","2","2.5","3","3.5","4","4.5","5","5.5","6"
];

const SwapOptionsPage: React.FC = () => {
  const year = useMemo(() => new Date().getFullYear(), []);
  const nav = useNavigate();

  const [intent, setIntent] = useState<"have" | "need" | "both">("both");
  const [size, setSize] = useState("");
  const [postcode, setPostcode] = useState("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onContinue() {
    setError(null);

    if (!size) return setError("Please select a boot size.");
    if (!postcode.trim()) return setError("Please enter a postcode.");

    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/swap-preferences`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          intent,
          bootSizeUk: size,
          postcode,
          source: "swap-options-v2",
        }),
      });

      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || `Request failed (${res.status})`);
      }

      // Route based on intent (MVP)
      if (intent === "need") nav("/find-swap-v2");
      else nav("/list-boots"); // have/both: supply-first
    } catch (e: any) {
      setError(e?.message || "Could not save your preferences. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="lp2">
      <main>
        <section className="lp2__section">
          <div className="lp2__container">
            <Link to="/" className="lp2__muted" style={{ fontSize: 14 }}>
              ← Back to home
            </Link>

            <h1 className="lp2__h2" style={{ fontSize: 34, marginTop: 12 }}>
              Swap options
            </h1>

            <p className="lp2__lead">
              Tell us what you’re looking for and we’ll prioritise matches near you.
            </p>

            <div className="lp2__grid2">
              <div className="lp2__panel">
                <h3>Your swap</h3>

                <div className="lp2__formStack">
                  <label className="lp2__field">
                    <span className="lp2__label">I…</span>
                    <select
                      className="lp2__control"
                      value={intent}
                      onChange={(e) => setIntent(e.target.value as any)}
                      disabled={saving}
                    >
                      <option value="both">have boots and need the next size</option>
                      <option value="have">have boots to swap</option>
                      <option value="need">need boots</option>
                    </select>
                  </label>

                  <label className="lp2__field">
                    <span className="lp2__label">Boot size (UK)</span>
                    <select
                      className="lp2__control"
                      value={size}
                      onChange={(e) => setSize(e.target.value)}
                      disabled={saving}
                    >
                      <option value="">Select size</option>
                      {BOOT_SIZES_UK.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </label>

                  <label className="lp2__field">
                    <span className="lp2__label">Postcode</span>
                    <input
                      className="lp2__control"
                      value={postcode}
                      onChange={(e) => setPostcode(e.target.value)}
                      placeholder="e.g. SS1 2AB"
                      disabled={saving}
                    />
                  </label>

                  {error && (
                    <div className="lp2__note" style={{ borderColor: "rgba(255,84,186,0.5)" }}>
                      {error}
                    </div>
                  )}

                  <button
                    type="button"
                    className="lp2__primaryBtn"
                    onClick={onContinue}
                    disabled={saving}
                  >
                    {saving ? "Saving…" : "Continue"}
                  </button>

                  <div className="lp2__note">
                    Next: {intent === "need" ? "we’ll show nearby boots" : "list boots to unlock matches"}.
                  </div>
                </div>
              </div>

              <div className="lp2__panel">
                <h3>How swaps will work</h3>
                <ol className="lp2__list" style={{ marginTop: 12 }}>
                  <li><strong>Local first</strong><span>Training/club handovers</span></li>
                  <li><strong>Clear condition notes</strong><span>No surprises</span></li>
                  <li><strong>Optional postage</strong><span>If local isn’t possible</span></li>
                </ol>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="lp2__footer">
        <div className="lp2__container lp2__footerInner">
          <div>© {year} BootLoop</div>
          <div className="lp2__footerLinks">
            <a href="/#how">How it works</a>
            <a href="/#waitlist">Waitlist</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SwapOptionsPage;
